import { useCallback, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ArcElement, Chart as ChartJS } from 'chart.js';
import { isNil, none, sum } from 'ramda';
import { Doughnut, getElementAtEvent } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { prefillFormAndSearch } from 'features/views/semaphore/actions';
import { halfDoughnutLabels as labels, halfDoughnutNameLabelMap } from 'features/views/semaphore/constants';
import { useClickChartDataLayer } from 'features/views/semaphore/hooks';
import { getGraphLastActualizationDate, getSemaphoreClientsTotal } from 'features/views/semaphore/selectors';
import { equalOrZero } from 'utils/equalOrThreeDashes';

import HalfDoughnutLegend from './HalfDoughnutLegend';

ChartJS.register(ArcElement);

export const HalfDoughnutGraph = () => {
	const dispatch = useDispatch();
	const pushToClickChartDataLayer = useClickChartDataLayer();

	const lastActualizationDate = useSelector(getGraphLastActualizationDate);
	const formattedActualizationDate = `Stav k ${lastActualizationDate}`;

	const semaphoreClientsTotal = useSelector(getSemaphoreClientsTotal);
	const orderedData = useMemo(
		() => [
			semaphoreClientsTotal?.green,
			semaphoreClientsTotal?.orange,
			semaphoreClientsTotal?.red,
			semaphoreClientsTotal?.grey,
		],

		[semaphoreClientsTotal],
	);

	const clientsSumAmount = sum(orderedData);
	const isSumBiggerThanZero = clientsSumAmount > 0;
	const formattedTotalSumPercentage = `${clientsSumAmount || 0} / 100 %`;

	const chartRef = useRef(null);
	const areDataToRenderGraphAvailable = none(isNil, orderedData);

	const { mode: theme } = useColorScheme();

	const generateData = useMemo(() => {
		if (isSumBiggerThanZero) {
			// used when we receive some actual data, not zeros
			return {
				labels,
				datasets: [
					{
						data: orderedData,
						backgroundColor: ['#27ae60', '#f57f17', '#c21b17', '#6f7072'],
					},
				],
			};
		} else {
			return {
				// used when we receive zeros
				// just placeholder data, to create "disabled" feel > graph with one full value and ligthGrey color
				datasets: [
					{
						data: [100],
						backgroundColor: ['lightGrey'],
					},
				],
			};
		}
	}, [isSumBiggerThanZero, orderedData]);

	const generatePlugins = useMemo(() => {
		if (isSumBiggerThanZero) {
			return [
				{
					id: 'halfDoughnut',
					beforeDraw(chart) {
						const { width } = chart;
						const { height } = chart;
						const { ctx } = chart;
						ctx.restore();

						const fontSize = (height / 180).toFixed(2);
						ctx.font = `${fontSize}em sans-serif`;
						ctx.textAlign = 'center';
						ctx.fillStyle = theme === 'light' ? 'black' : 'white';
						ctx.fillText(formattedActualizationDate, width / 2, height / 2 + 20);
						ctx.restore();

						ctx.font = `${fontSize}em sans-serif`;
						ctx.textAlign = 'center';
						ctx.fillText(formattedTotalSumPercentage, width / 2, height / 2 + 40);
						ctx.save();
					},
				},
			];
		} else {
			return [
				{
					id: 'halfDoughnut',
					beforeDraw(chart) {
						const { width } = chart;
						const { height } = chart;
						const { ctx } = chart;
						ctx.restore();
						const fontSize = (height / 200).toFixed(2);
						ctx.font = `${fontSize}em sans-serif`;
						ctx.fillStyle = theme === 'light' ? 'black' : 'white';
						const text = getLabelMessage('semaphore:graphCurrentStatusNoData');
						const textX = Math.round((width - ctx.measureText(text).width) / 2);
						const textY = height / 2 + 30;
						ctx.fillText(text, textX, textY);
						ctx.save();
					},
				},
			];
		}
	}, [theme, formattedActualizationDate, formattedTotalSumPercentage, isSumBiggerThanZero]);

	const options = {
		rotation: -90,
		circumference: 180,
		cutout: '75%',
		maintainAspectRatio: false,
		plugins: {
			tooltip: {
				enabled: false,
			},
		},
		onHover: (evt, activeEls) => (evt.chart.canvas.style.cursor = activeEls.length > 0 ? 'pointer' : 'default'),
	};

	const onClickFn = useCallback(
		(event) => {
			const { current: chart } = chartRef;
			const element = getElementAtEvent(chart, event);
			const { index } = element[0];
			const clickedPartOfGraph = labels[index];
			const clickedGraphNameLabel = halfDoughnutNameLabelMap[clickedPartOfGraph];

			pushToClickChartDataLayer({ value: clickedGraphNameLabel })();
			isSumBiggerThanZero && dispatch(prefillFormAndSearch('halfDoughnut', clickedPartOfGraph));
		},
		[dispatch, isSumBiggerThanZero, pushToClickChartDataLayer],
	);

	const calculatePercentage = (amount, total) => equalOrZero(Math.round((amount / total) * 100));

	const legendData = [
		{
			amount: equalOrZero(semaphoreClientsTotal.green),
			name: 'OneToTwoYears',
			percentage: calculatePercentage(semaphoreClientsTotal?.green, clientsSumAmount),
		},
		{
			amount: equalOrZero(semaphoreClientsTotal.orange),
			name: 'ThreeToFourYears',
			percentage: calculatePercentage(semaphoreClientsTotal?.orange, clientsSumAmount),
		},
		{
			amount: equalOrZero(semaphoreClientsTotal.red),
			name: 'FiveToSixYears',
			percentage: calculatePercentage(semaphoreClientsTotal?.red, clientsSumAmount),
		},
		{
			amount: equalOrZero(semaphoreClientsTotal.grey),
			name: 'SevenAndLaterYears',
			percentage: calculatePercentage(semaphoreClientsTotal?.grey, clientsSumAmount),
		},
	];

	return (
		<Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
			<Stack
				direction="row"
				sx={{
					alignItems: 'center',
				}}
			>
				<Typography variant="h5" color="text.primary">
					{getLabelMessage('semaphore:graphCurrentStatus')}
				</Typography>
				<InfoCircleTooltip sx={{ fontSize: 15 }} text={getLabelMessage('tooltips:semaphoreCurrentStatus')} />
			</Stack>
			<Stack sx={{ height: '100%' }}>
				<Box sx={{ height: '70%' }}>
					{areDataToRenderGraphAvailable && (
						<Doughnut
							ref={chartRef}
							data={generateData}
							options={options}
							onClick={onClickFn}
							plugins={generatePlugins}
							redraw
						/>
					)}
				</Box>
				<Stack sx={{ px: 15 }}>{isSumBiggerThanZero && <HalfDoughnutLegend data={legendData} />}</Stack>
			</Stack>
		</Paper>
	);
};

export default HalfDoughnutGraph;
